import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  getGoldenBoot,
  getMyGroupWinnerPicks,
  getQualificationProjection,
  submitGoldenBootPick,
  submitGroupWinnerPrediction,
} from '@/lib/data/provider';

/** "If the groups ended now" advancers, computed from the live standings. */
export function useQualificationProjection(
  competitionId: string | undefined,
  season: number | undefined,
) {
  return useQuery({
    queryKey: ['qualification', competitionId ?? 'none', season ?? 0],
    queryFn: () => getQualificationProjection(competitionId as string, season as number),
    staleTime: QUERY_STALE_TIME.fixtures,
    enabled: isSupabaseConfigured && Boolean(competitionId) && season !== undefined,
  });
}

/** The Golden Boot live race + the caller's pick. */
export function useGoldenBoot(seasonId: string | undefined) {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['golden-boot', seasonId ?? 'none', userId ?? 'anon'],
    queryFn: () => getGoldenBoot(seasonId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(seasonId),
  });
}

export function useSubmitGoldenBoot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { seasonId: string; playerId: string }) =>
      submitGoldenBootPick(input.seasonId, input.playerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['golden-boot'] }),
  });
}

/** My saved "pick each group's winner" predictions for a competition. */
export function useMyGroupWinnerPicks(competitionId: string | undefined) {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['group-winners', competitionId ?? 'none', userId ?? 'anon'],
    queryFn: () => getMyGroupWinnerPicks(competitionId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(competitionId) && Boolean(userId),
  });
}

export function useSubmitGroupWinnerPrediction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { competitionId: string; teamIds: string[] }) =>
      submitGroupWinnerPrediction(input.competitionId, input.teamIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['group-winners'] }),
  });
}
